import { Fragment, type ReactElement } from 'react';
import { getBuildStatusIconComponent } from '@/features/shared/components/tableSystem/buildStatusIcons';
import {
  TABLE_BREAKDOWN_STACK_ROW_GAP,
  TableBreakdownLine,
} from '@/features/shared/components/tableSystem/TableBreakdownLine';
import { TableMetaStack } from '@/features/shared/components/tableSystem/TableMetaCells';
import { SummaryTooltipContent } from '@/ui/composites/table/cells/summaryCellUtils';
import { Box, BuildsIcon, Tooltip } from '@/ui/primitives';
import type { SummaryTooltipItem } from '@/ui/composites/table/tableUtils';
import type { BuildStatus } from '@flowqa/shared';

type TableBuildStatusBreakdownCellProps = {
  total: number;
  counts: Partial<Record<BuildStatus, number>> | null | undefined;
  summaryItems?: SummaryTooltipItem[];
};

const BREAKDOWN_TOOLTIP_ENTER_DELAY_MS = 250;

const formatPercent = (count: number, total: number): string => {
  if (total <= 0) return '0%';
  return `${Math.round((count / total) * 100)}%`;
};

const getBuildStatusIconColor = (status: BuildStatus): string => {
  if (status === 'completed') return 'success.main';
  if (status === 'failed') return 'error.main';
  if (status === 'running') return 'info.main';
  return 'text.secondary';
};

export const TableBuildStatusBreakdownCell = ({
  total,
  counts,
  summaryItems = [],
}: TableBuildStatusBreakdownCellProps): ReactElement => {
  const rows: BuildStatus[] = ['completed', 'failed', 'running', 'queued'];
  const visibleRows = rows.filter((status) => (counts?.[status] ?? 0) > 0);
  const renderTooltipRow = ({
    icon,
    content,
    items,
  }: {
    icon: ReactElement;
    content: string;
    items: SummaryTooltipItem[];
  }): ReactElement => (
    <Tooltip
      title={
        <SummaryTooltipContent
          items={items}
          linkMode="execution"
          skippedIconVariant="outlined"
          showDetailLabel={false}
        />
      }
      disableHoverListener={items.length === 0}
      enterDelay={BREAKDOWN_TOOLTIP_ENTER_DELAY_MS}
      enterNextDelay={BREAKDOWN_TOOLTIP_ENTER_DELAY_MS}
      arrow
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 'none',
            width: 'fit-content',
            fontSize: '15px',
            lineHeight: 1.4,
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: 'fit-content',
          maxWidth: '100%',
          alignSelf: 'flex-start',
          lineHeight: 1,
        }}
      >
        <TableBreakdownLine icon={icon} content={content} />
      </Box>
    </Tooltip>
  );

  return (
    <TableMetaStack rowGap={TABLE_BREAKDOWN_STACK_ROW_GAP}>
      {renderTooltipRow({
        icon: (
          <BuildsIcon
            sx={{
              fontSize: (theme) => theme.app.iconSizes.sm,
              flexShrink: 0,
              color: 'text.secondary',
              transform: 'translateY(2px)',
            }}
          />
        ),
        content: `${total.toLocaleString()} ${total === 1 ? 'build' : 'builds'}`,
        items: summaryItems,
      })}
      {visibleRows.map((status) => {
        const count = counts?.[status] ?? 0;
        const StatusIcon = getBuildStatusIconComponent(status);
        const statusSummaryItems = summaryItems.filter((item) => item.status === status);
        return (
          <Fragment key={status}>
            {renderTooltipRow({
              icon: (
                <StatusIcon
                  sx={{
                    fontSize: (theme) => theme.app.iconSizes.sm,
                    flexShrink: 0,
                    color: getBuildStatusIconColor(status),
                    transform: 'translateY(2px)',
                  }}
                />
              ),
              content: `${count.toLocaleString()} ${status.toLowerCase()} (${formatPercent(count, total)})`,
              items: statusSummaryItems,
            })}
          </Fragment>
        );
      })}
    </TableMetaStack>
  );
};
